import * as React from "react";
import { SearchRegular, ChevronRightRegular, ChevronDownRegular, PlayRegular, ArrowSortDownRegular, ArrowSortUpRegular } from "@fluentui/react-icons";
import { primer } from "../styles/primerDarkTheme";
import { useTestResultsTableStyles } from "../styles/testResultsTable.styles";
import { DisplayTestStatus } from "../types/TestCasesTab.types";
import { STATUS_BADGE, STATUS_ICON, STATUS_LABEL, FILTERS, statusMatchesFilter, type FilterKey } from "../constants/statusMapping";
import type { TestCase, TestSuite } from "../models/TestResultModels";
import type { ProjectVideoFile } from "../api/models/ApiModels";
import type { TestHistoryPoint } from "../types/ProjectDashboard.types";
import TestSparkline from "./TestSparkline";
import { findRecordingForVideoPath } from "../utils/recordings";

const TAG_COLORS: Record<string, string> = {
    happy_path:  "#58a6ff",
    error_path:  "#f2994a",
    auto_healed: "#56ccf2",
    "@happy":    "#58a6ff",
    "@error":    "#f2994a",
    "@healed":   "#56ccf2",
};

const TAG_LABELS: Record<string, string> = {
    happy_path:  "Happy path",
    error_path:  "Error path",
    auto_healed: "Auto healed",
    "@happy":    "Happy path",
    "@error":    "Error path",
    "@healed":   "Auto healed",
};

// Make a clickable, non-semantic element keyboard-activatable via Enter/Space.
const activateOnKey = (handler: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handler();
    }
};

type Filter = FilterKey;

/* ── Sparkline ── */
function TrendBars({ tc, crossRunHistory }: { tc: TestCase; crossRunHistory?: Map<string, TestHistoryPoint[]> }) {
    const history = crossRunHistory?.get(tc.historyKey) ?? [];
    // Append the current run's status as the last point
    const withCurrent: TestHistoryPoint[] = [
        ...history,
        { runId: "current", completedAt: "", status: tc.displayStatus, duration: 0 },
    ];
    return <TestSparkline history={withCurrent} maxPoints={10} />;
}

/* ── Main component ── */
interface Props {
    suites: TestSuite[];
    onWatchVideo?: (tc: TestCase) => void;
    videoFiles?: ProjectVideoFile[];
    crossRunHistory?: Map<string, TestHistoryPoint[]>;
}

export const TestResultsTable: React.FC<Props> = ({ suites, onWatchVideo, videoFiles, crossRunHistory }) => {
    const s = useTestResultsTableStyles();
    const [filters, setFilters] = React.useState<Set<Filter>>(new Set());
    const [search, setSearch] = React.useState("");
    const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
    const [sortCol, setSortCol] = React.useState<"status" | "duration" | null>(null);
    const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");

    const toggleSort = (col: "status" | "duration") => {
        if (sortCol === col) {
            if (sortDir === "asc") setSortDir("desc");
            else { setSortCol(null); setSortDir("asc"); } // third click clears
        } else {
            setSortCol(col);
            setSortDir("asc");
        }
    };

    const toggleFilter = (value: Filter) => {
        if (value === "all") { setFilters(new Set()); return; }
        setFilters(prev => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value); else next.add(value);
            return next;
        });
    };

    const toggleCollapse = (title: string) => setCollapsed(prev => {
        const next = new Set(prev);
        if (next.has(title)) next.delete(title); else next.add(title);
        return next;
    });

    // Use the same authoritative resolver the click handler uses
    // (findRecordingForVideoPath in handleWatchVideo). A test row only gets the
    // "Watch Test" button if we can actually resolve a playable recording URL —
    // otherwise the button used to render but click silently no-op'd.
    const hasVideoForTest = (tc: TestCase) =>
        !!(tc.primaryVideoPath && findRecordingForVideoPath(tc.primaryVideoPath, videoFiles ?? [])?.url);

    const allTests = React.useMemo(() => suites.flatMap(su => su.allTestCases), [suites]);

    const counts = React.useMemo(() => {
        const result: Record<FilterKey, number> = { all: allTests.length, passed: 0, failed: 0, flaky: 0, skipped: 0 };
        for (const t of allTests) {
            for (const f of FILTERS) {
                if (f.key !== "all" && f.statuses.includes(t.displayStatus)) result[f.key]++;
            }
        }
        return result;
    }, [allTests]);

    const STATUS_ORDER: Record<string, number> = {
        [DisplayTestStatus.Failed]: 0,
        [DisplayTestStatus.Flaky]: 1,
        [DisplayTestStatus.Skipped]: 2,
        [DisplayTestStatus.Passed]: 3,
        [DisplayTestStatus.NoResults]: 4,
        [DisplayTestStatus.Unknown]: 5,
    };

    const filteredSuites = React.useMemo(() => {
        const q = search.toLowerCase();

        const compareFn = (a: TestCase, b: TestCase): number => {
            if (!sortCol) return 0;
            if (sortCol === "duration") {
                // Zero duration = no data → sink to bottom
                if (a.duration === 0 && b.duration === 0) return 0;
                if (a.duration === 0) return 1;
                if (b.duration === 0) return -1;
                const cmp = a.duration - b.duration;
                return sortDir === "desc" ? -cmp : cmp;
            }
            // status — all tests have a status, no empties to sink
            const cmp = (STATUS_ORDER[a.displayStatus] ?? 9) - (STATUS_ORDER[b.displayStatus] ?? 9);
            return sortDir === "desc" ? -cmp : cmp;
        };

        const groups = suites
            .map(suite => {
                let tests = suite.allTestCases;
                if (filters.size > 0) {
                    tests = tests.filter(t =>
                        [...filters].some(key => statusMatchesFilter(t.displayStatus, key))
                    );
                }
                if (q) tests = tests.filter(t => t.title.toLowerCase().includes(q));
                if (sortCol) tests = [...tests].sort(compareFn);
                return { suite, tests };
            })
            .filter(g => g.tests.length > 0);

        // Sort groups by the "best" (lowest order) test in each group; sink data-less groups
        if (sortCol) {
            const groupScore = (tests: TestCase[]): number => {
                const scores = tests.map(t => {
                    if (sortCol === "status") return STATUS_ORDER[t.displayStatus] ?? 9;
                    return t.duration || Infinity;
                });
                return Math.min(...scores);
            };
            groups.sort((a, b) => {
                const scoreA = groupScore(a.tests);
                const scoreB = groupScore(b.tests);
                if (scoreA === Infinity && scoreB === Infinity) return 0;
                if (scoreA === Infinity) return 1;
                if (scoreB === Infinity) return -1;
                return sortDir === "desc" ? scoreB - scoreA : scoreA - scoreB;
            });
        }

        return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suites, filters, search, sortCol, sortDir]);

    const filterOpts = FILTERS.map(f => ({
        ...f,
        icon: f.statuses.length > 0 ? STATUS_ICON[f.statuses[0]] : undefined,
    }));

    const formatDuration = (ms: number) => {
        if (ms === 0) return "—";
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    };

    return (
        <div className={s.root}>
            {/* Toolbar */}
            <div className={s.toolbar}>
                <div className={s.searchBox}>
                    <span className={s.searchIcon}><SearchRegular style={{ fontSize: 14 }} /></span>
                    <input
                        className={s.searchInput}
                        placeholder="Search"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className={s.pills}>
                    {filterOpts.map(f => {
                        const isActive = f.key === "all" ? filters.size === 0 : filters.has(f.key);
                        return (
                            <button
                                key={f.key}
                                className={`${s.pill} ${isActive ? s.pillActive : ""}`}
                                onClick={() => toggleFilter(f.key)}
                            >
                                {f.icon && <f.icon.icon style={{ fontSize: 14, color: f.icon.color }} />}
                                <span>{f.label} ({counts[f.key]})</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={s.divider} />

            {/* Column headers */}
            <div className={`${s.grid} ${s.colHeader}`}>
                <span className={s.colText}>Test Name</span>
                <span
                    className={`${s.colText} ${s.colSortable}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Sort by status${sortCol === "status" ? `, ${sortDir === "asc" ? "ascending" : "descending"}` : ""}`}
                    onClick={() => toggleSort("status")}
                    onKeyDown={activateOnKey(() => toggleSort("status"))}
                >
                    Status {sortCol === "status" && (sortDir === "asc" ? <ArrowSortUpRegular style={{ fontSize: 10 }} /> : <ArrowSortDownRegular style={{ fontSize: 10 }} />)}
                </span>
                <span
                    className={`${s.colText} ${s.colSortable}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Sort by duration${sortCol === "duration" ? `, ${sortDir === "asc" ? "ascending" : "descending"}` : ""}`}
                    onClick={() => toggleSort("duration")}
                    onKeyDown={activateOnKey(() => toggleSort("duration"))}
                >
                    Duration {sortCol === "duration" && (sortDir === "asc" ? <ArrowSortUpRegular style={{ fontSize: 10 }} /> : <ArrowSortDownRegular style={{ fontSize: 10 }} />)}
                </span>
                <span className={s.colText}>Trend</span>
                <span className={s.colText}>Last Run</span>
                <span className={s.colText}>Video</span>
            </div>

            <div className={s.dividerMuted} />

            {/* Suite groups */}
            {filteredSuites.map(({ suite, tests }) => {
                const isCollapsed = collapsed.has(suite.title);
                return (
                <div key={suite.title}>
                    <div
                        className={s.groupHeader}
                        role="button"
                        tabIndex={0}
                        aria-expanded={!isCollapsed}
                        aria-label={`${isCollapsed ? "Expand" : "Collapse"} suite ${suite.title}`}
                        onClick={() => toggleCollapse(suite.title)}
                        onKeyDown={activateOnKey(() => toggleCollapse(suite.title))}
                    >
                        <span className={s.groupArrow}>{isCollapsed ? <ChevronRightRegular style={{ fontSize: 10 }} /> : <ChevronDownRegular style={{ fontSize: 10 }} />}</span>
                        <span className={s.groupName}>{suite.title}</span>
                        <span className={s.groupCount}>{tests.length}</span>
                    </div>
                    {!isCollapsed && tests.map(tc => {
                        const st = tc.displayStatus;
                        const badge = STATUS_BADGE[st] ?? STATUS_BADGE[DisplayTestStatus.Unknown];
                        const icon = STATUS_ICON[st] ?? STATUS_ICON[DisplayTestStatus.Unknown];
                        const isFailed = st === DisplayTestStatus.Failed;
                        const isSkipped = st === DisplayTestStatus.Skipped || st === DisplayTestStatus.NoResults;
                        const statusLabel = STATUS_LABEL[st] ?? st;

                        return (
                            <div key={tc.id} className={`${s.grid} ${s.row} ${isFailed ? s.rowFailed : ""} ${isSkipped ? s.rowSkipped : ""}`}>
                                <div className={s.nameCell}>
                                    <icon.icon style={{ fontSize: 16, color: icon.color, flexShrink: 0 }} />
                                    <span className={s.testName}>{tc.title}</span>
                                    {tc.tags.map(tag => {
                                        const color = TAG_COLORS[tag] ?? primer.fgMuted;
                                        const label = TAG_LABELS[tag] ?? tag.replace("@", "");
                                        return (
                                            <span
                                                key={tag}
                                                className={s.tag}
                                                style={{ borderColor: color, color }}
                                            >
                                                {label}
                                            </span>
                                        );
                                    })}
                                </div>
                                <div className={s.statusCell}>
                                    <span className={s.badge} style={{ backgroundColor: badge.bg, color: badge.text }}>
                                        {statusLabel}
                                    </span>
                                </div>
                                <div className={s.durCell}>{formatDuration(tc.duration)}</div>
                                <div className={s.trendCell}>
                                    <TrendBars tc={tc} crossRunHistory={crossRunHistory} />
                                </div>
                                <div className={s.lastRunCell}>
                                    {tc.results.length > 0 && tc.results[tc.results.length - 1].startTime
                                        ? formatTimeAgo(tc.results[tc.results.length - 1].startTime!)
                                        : "—"}
                                </div>
                                <div className={s.playbackCell}>
                                    {hasVideoForTest(tc) && (
                                        <button className={s.watchBtn} onClick={() => onWatchVideo?.(tc)}>
                                            <span className={s.watchBtnIcon}><PlayRegular style={{ fontSize: 10 }} /></span>
                                            Watch test
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    </div>
                );
            })}
        </div>
    );
};

function formatTimeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}
